import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { FC, useContext, useCallback, useState, JSX, useEffect } from "react";
import TemplateService from "@app/admin/manage-templates/templateService";
import DragAndDropFileUploader from "@app/shared/DragAndDropFileUploader";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { TemplateMetadataModel } from "@app/admin/manage-templates/template";
import { TemplateDetailsData } from "@app/admin/manage-specialties/templateDetailsData";

interface UploadTemplateFlyoutProps {
  onUpload: () => void;
  setUploadSuccess: (uploadSuccess: boolean) => void;
  template: TemplateMetadataModel;
  userId: number;
}

const UploadTemplateFlyout: FC<UploadTemplateFlyoutProps> = ({ onUpload, setUploadSuccess, template, userId }: UploadTemplateFlyoutProps): JSX.Element => {
  const flyoutContext = useContext(FlyoutContext);
  const [newTemplateDetails, setNewTemplateDetails] = useState<TemplateDetailsData>();
  const [loading, setLoading] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const [validUpload, setValidUpload] = useState<boolean>(true);
  const [hasFile, setHasFile] = useState<boolean>(false);
  const [validComment, setValidComment] = useState<boolean>(false);

  const defaultComment = `Date Updated: ${Intl.DateTimeFormat("en-US", { dateStyle: 'short', timeStyle: 'short' }).format(new Date())}\nChanges Made: \nOther Comments: `;

  useEffect(() => { validateComment() }, [comment])

  const validateComment = async () => {
    const isValid =
      comment.length <= 1000
      && comment.length > 0
      && comment !== defaultComment;
    setValidComment(isValid);
  }

  const handleSave = async () => {
    setLoading(true);
    const templateService = new TemplateService();
    try {
      if (hasFile) {
        newTemplateDetails.creationComment = comment;
        await templateService.addTemplateJson(newTemplateDetails).then(() => { flyoutContext.handleCloseFlyout() }).then(() => { setLoading(false); setUploadSuccess(true); });
      } else {
        await templateService.addTemplateComment(template.templateCollectionId, template.templateId, comment).then(() => { flyoutContext.handleCloseFlyout() }).then(() => { setLoading(false); setUploadSuccess(true); })
      }
    } catch {
      setUploadSuccess(false);
    }
  };

  const handleUpload = useCallback(async (data: FormData): Promise<void> => {
    await readFile(data).then((result) => {
      if (!result) {
        return Promise.reject("Template name mismatch. Please make sure the uploaded template's DisplayName field matches the name of the template you are attempting to update.");
      }
      setHasFile(true);
      return Promise.resolve();
    });
  }, [onUpload, flyoutContext]);

  const readFile = async (data: FormData): Promise<boolean> => {
    let result: TemplateDetailsData;
    const info: FormDataEntryValue = data.get("templateJsonFile");
    if (info instanceof File) {
      const reader = new FileReader();

      return new Promise<boolean>((resolve, reject) => {
        reader.onload = () => {
          try {
            result = JSON.parse(reader.result.toString());

            // Set id values to values that are used in the current env to prevent uploading Ids from other envs
            result.accessCodeMapSetId = template.accessCodeMapSetId;
            result.accessCodeMapSetVersionId = template.accessCodeMapSetVersionId;
            result.memberSetId = template.memberSetId;
            result.attestationCrosswalkCodeMapSetId = template.attestationCrosswalkCodeMapSetId;
            result.templateId = template.templateId;
            result.templateCollectionId = template.templateCollectionId;
            result.correlationId = template.correlationId;
            result.motiveCrosswalkCodeMapSetId = template.motiveCrosswalkCodeMapSetId;
            result.createdByUserId = userId;
            setNewTemplateDetails(result);

            const templateNameMatch: boolean = result.displayName === template.templateName
            setValidUpload(templateNameMatch);
            return resolve(templateNameMatch);
          } catch {
            return resolve(false);
          }
        }
        reader.onerror = () => { reject(false); };
        reader.readAsText(info);
      });
    }
  }

  return (
    loading ? <LoadingSpinner /> :
    <>
      <div className="mb-4">Select and upload a JSON file. Unsupported file types will not be accepted.</div>
      <DragAndDropFileUploader
        apiKeyName="templateJsonFile"
        title=""
        filetype=".json"
        onUpload={handleUpload}
        small={true}
        testId="manage-templates-list__file-upload"
        />
      <QESTextArea
        containerClassName="upload-template-flyout__comment mt-3"
        label="Comment:"
        testId="upload-template-flyout__comment"
        maxLength={1000}
        defaultValue={defaultComment}
        rows={4}
        showCounter={false}
        onChange={(e) => setComment(e.target.value)}
      />
      <FlyoutFooter
        submitButton={<QESButton
          onClick={handleSave}
          disabled={(hasFile && !newTemplateDetails) || !validUpload || !validComment}
          qesTestId="upload-template__save-button"
        >Save</QESButton>}
      />
    </>
  );
};

export default UploadTemplateFlyout;